import type { ComponentsUsingTool } from '@reach/shared-types/agent-components';
import { ConfirmDialog } from '../../../components/common/ConfirmDialog';
import { ComponentsUsingToolList } from './ComponentsUsingToolList';

interface DeactivateToolDialogProps {
  toolName: string;
  componentsUsingTool: ComponentsUsingTool | undefined;
  onConfirm: () => void;
  onClose: () => void;
}

export const DeactivateToolDialog = ({
  toolName,
  componentsUsingTool,
  onConfirm,
  onClose,
}: DeactivateToolDialogProps) => {
  const publishedComponents = componentsUsingTool?.published ?? [];
  const draftOnlyComponents = componentsUsingTool?.draftOnly ?? [];
  const toolIsPublished = publishedComponents.length > 0;
  const toolIsOnDraft = draftOnlyComponents.length > 0;

  if (toolIsPublished) {
    const publishedMessage = (
      <>
        <strong>{toolName}</strong> is used by your synced template. Remove it from these components and
        sync the template, then deactivate it.
      </>
    );
    return (
      <ConfirmDialog
        title="Can't deactivate this tool yet"
        confirmLabel="Deactivate"
        onConfirm={onConfirm}
        onClose={onClose}
        disabled>
        <p>{publishedMessage}</p>
        <ComponentsUsingToolList components={publishedComponents} />
      </ConfirmDialog>
    );
  }

  const deactivationMessage = (
    <>
      Deactivating <strong>{toolName}</strong> removes it from your agents — they will no longer be able
      to use this tool.
    </>
  );
  return (
    <ConfirmDialog
      title="Deactivate tool?"
      confirmLabel="Deactivate"
      onConfirm={onConfirm}
      onClose={onClose}>
      <p>{deactivationMessage}</p>
      {toolIsOnDraft && (
        <>
          <p>
            These components in your template&apos;s draft still use it. Remove it from them before your
            next sync:
          </p>
          <ComponentsUsingToolList components={draftOnlyComponents} />
        </>
      )}
      <p>You can reactivate it anytime from the tool&apos;s Status.</p>
    </ConfirmDialog>
  );
};
